import { useEffect, useMemo, useState } from 'react';
import { usePortfolioOverview } from './usePortfolioOverview';
import { usePortfolioHistory } from './usePortfolioHistory';
import { buildPerformance, deriveInitialPrincipal, isAccountUnfunded } from './portfolioUtils';
import PortfolioShell from './PortfolioShell';
import PortfolioHeroChart from './PortfolioHeroChart';
import PortfolioCandlesChart from './PortfolioCandlesChart';
import { buildPortfolioCandles } from './portfolioCandles';
import PortfolioUnfundedHero from './PortfolioUnfundedHero';
import PortfolioActionsBar from './PortfolioActionsBar';
import PortfolioAllocationChart from './PortfolioAllocationChart';
import PortfolioPositionsTable, { PositionRow } from './PortfolioPositionsTable';
import PortfolioRebalancingPanel from './PortfolioRebalancingPanel';
import PortfolioStockDrawer from './PortfolioStockDrawer';

const PortfolioDashboardPage = () => {
  const {
    account,
    allAccounts,
    content,
    accountsLoading,
    contentLoading,
    contentError,
    updating,
    rebalancing,
    selectAccount,
    handleRefresh,
    handleRebalance,
    handleToggleActive,
    contentUpdatedAt,
  } = usePortfolioOverview();

  const performance = useMemo(() => buildPerformance(content), [content]);

  const historyAnchor = useMemo(
    () => ({
      tradingStartedAt:
        content?.tradingStartedAt ?? content?.trackRecordStartedAt,
      initialPrincipal: deriveInitialPrincipal(content),
    }),
    [content],
  );

  const {
    selectedRange,
    history,
    loading: historyLoading,
    error: historyError,
    selectRange,
  } = usePortfolioHistory(
    account?.id ?? null,
    historyAnchor,
    !contentLoading && content !== undefined,
  );

  const [selectedPosition, setSelectedPosition] = useState<PositionRow | null>(null);
  const [candleMode, setCandleMode] = useState<'$' | '%'>('$');

  const candleData = useMemo(
    () => buildPortfolioCandles(history, selectedRange),
    [history, selectedRange],
  );

  const signalForgeEnabled = account?.signal_forge_enabled === true;

  // Clear any open stock panel when switching accounts.
  useEffect(() => {
    setSelectedPosition(null);
  }, [account?.id]);

  if (accountsLoading || !account) {
    return (
      <div className="flex min-h-0 flex-col items-center justify-center bg-background bg-[image:var(--nq-canvas-glow)] italic text-muted-foreground">
        <p>Loading portfolio…</p>
      </div>
    );
  }

  const accountTitle =
    account.nickname ||
    content?.accountName ||
    (account.isPaper ? 'Paper Trading' : 'Live Trading');

  const isUnfunded = !contentLoading && content !== undefined && isAccountUnfunded(content);

  return (
    <PortfolioShell>
      <PortfolioActionsBar
        account={account}
        allAccounts={allAccounts}
        accountTitle={accountTitle}
        contentLoading={contentLoading}
        lastUpdatedAt={contentUpdatedAt}
        updating={updating}
        rebalancing={rebalancing}
        isUnfunded={isUnfunded}
        onSelectAccount={selectAccount}
        onRefresh={handleRefresh}
        onRebalance={handleRebalance}
        onToggleActive={handleToggleActive}
      />

      {contentError && (
        <div className="mb-4 rounded-lg border border-[color:var(--nq-destructive-border)] bg-[color:var(--nq-destructive-soft)] px-4 py-3 text-sm text-[color:var(--nq-down-soft)]">
          {contentError}
        </div>
      )}

      {isUnfunded && content ? (
        <PortfolioUnfundedHero
          content={content}
          onRefresh={handleRefresh}
          refreshing={contentLoading}
          lastUpdatedAt={contentUpdatedAt}
        />
      ) : (
        <PortfolioHeroChart
          content={content}
          performance={performance}
          loading={contentLoading && !content}
          history={history}
          historyLoading={historyLoading}
          historyError={historyError}
          selectedRange={selectedRange}
          onRangeChange={selectRange}
        />
      )}

      {!isUnfunded && (
        <section className="mb-6">
          <PortfolioCandlesChart
            candles={candleData.candles}
            benchmark={candleData.benchmark}
            benchmarkAvailable={candleData.benchmarkAvailable}
            mode={candleMode}
            onModeChange={setCandleMode}
            loading={historyLoading && candleData.candles.length === 0}
            error={historyError}
          />
        </section>
      )}

      <div className="grid grid-cols-1 items-start gap-5 min-[1100px]:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          <PortfolioPositionsTable
            content={content}
            loading={contentLoading && !content}
            error={contentError}
            isUnfunded={isUnfunded}
            selectedSymbol={!isUnfunded ? selectedPosition?.symbol ?? null : null}
            onPositionSelect={!isUnfunded ? setSelectedPosition : undefined}
          />
        </div>
        <aside className="flex flex-col gap-4">
          <PortfolioAllocationChart content={content} isUnfunded={isUnfunded} />
          <PortfolioRebalancingPanel
            account={account}
            content={content}
            isUnfunded={isUnfunded}
          />
        </aside>
      </div>

      <PortfolioStockDrawer
        open={selectedPosition !== null}
        position={selectedPosition}
        accountId={account.id}
        signalForgeEnabled={signalForgeEnabled}
        tradingStartedAt={content?.tradingStartedAt ?? content?.trackRecordStartedAt}
        onClose={() => setSelectedPosition(null)}
      />
    </PortfolioShell>
  );
};

export default PortfolioDashboardPage;
